import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Masthead } from "@/components/Masthead";
import { Footer } from "@/components/Footer";
import { ArticleModal } from "@/components/ArticleModal";
import { StoryCard } from "@/components/StoryCard";
import { SectionLabel } from "@/components/SectionLabel";
import { ArrowLeft, BookOpen } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SharedListPage = () => {
  const { listId } = useParams();
  const navigate = useNavigate();
  const [sharedList, setSharedList] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    fetchSharedList();
  }, [listId]);

  const fetchSharedList = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/shared/${listId}`);
      setSharedList(response.data);
    } catch (error) {
      console.error("Error fetching shared list:", error);
      setError("This reading list could not be found.");
    } finally {
      setLoading(false);
    }
  };

  const openArticle = (article) => {
    setSelectedArticle(article);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedArticle(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-scoop-cream flex items-center justify-center">
        <div className="text-center">
          <div className="font-display text-3xl font-bold text-scoop-ink mb-2">
            The <em className="text-scoop-blue">Scoop</em>
          </div>
          <p className="text-scoop-muted text-sm">Loading reading list...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="scoop-page min-h-screen bg-scoop-cream" data-testid="shared-page">
        <Masthead activeCategory="" onCategoryChange={() => navigate("/")} />
        
        <div className="max-w-[600px] mx-auto px-5 py-16 text-center">
          <div className="bg-white rounded-lg border border-scoop-border p-8">
            <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h2 className="font-display text-2xl font-bold text-scoop-ink mb-3">
              Reading List Not Found
            </h2>
            <p className="text-scoop-muted mb-6">{error}</p>
            <button
              onClick={() => navigate("/")}
              className="text-scoop-blue hover:underline font-medium"
            >
              Go to homepage
            </button>
          </div>
        </div>
        
        <Footer />
      </div>
    );
  }

  return (
    <div className="scoop-page min-h-screen bg-scoop-cream" data-testid="shared-page">
      <Masthead activeCategory="" onCategoryChange={() => navigate("/")} />
      
      <div className="max-w-[1200px] mx-auto px-5 md:px-10 py-8">
        <button 
          onClick={() => navigate("/")}
          className="flex items-center gap-2 text-scoop-muted hover:text-scoop-blue mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Go to The Scoop homepage
        </button>
        
        {/* Header */}
        <div className="bg-white rounded-lg border border-scoop-border p-6 mb-8">
          <div className="flex items-center gap-3 mb-2">
            <BookOpen className="w-6 h-6 text-scoop-blue" />
            <span className="text-sm font-semibold text-scoop-muted uppercase tracking-wide">
              Shared Reading List
            </span>
          </div>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-scoop-ink mb-2">
            {sharedList.title}
          </h1>
          <p className="text-scoop-muted">
            Curated by <strong>{sharedList.shared_by}</strong> · {sharedList.articles?.length || 0} stories
          </p>
        </div>
        
        {sharedList.articles && sharedList.articles.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {sharedList.articles.map((article, index) => (
              <StoryCard 
                key={article.id} 
                article={article} 
                onClick={() => openArticle(article)}
                delay={index + 1}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-scoop-muted">This reading list is empty.</p>
          </div>
        )}
      </div>
      
      <Footer />
      
      <ArticleModal 
        article={selectedArticle} 
        isOpen={modalOpen} 
        onClose={closeModal}
      />
    </div>
  );
};

export default SharedListPage;
